import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Box, Typography, Switch, FormControlLabel, FormGroup,
  Select, Chip, MenuItem, OutlinedInput, SelectChangeEvent, Divider,
  Tabs, Tab, Paper, Button, TextField, List, ListItem, ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  Grid,
  CardContent,
  Card,
  CardHeader,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useChat } from '../../context/chatContext';
import EnterpriseModal from './Enterprise';
import KnowledgebaseTable from './knowledgebaseTable';
import FileUpload from './fileUpload';
import { Dataset } from '../../types';
import { getNotes, listAllDatasets, refineNotes } from '../../services/api';
import ManageMemories from './manageMemories';
import ChainOfThought from '../chat/chainOfThought';
import RefreshIcon from '@mui/icons-material/RefreshRounded'
import ManagePersonalities from './managePersonalities';
import ThinkingStreamPanel from './thinkingStreamPanel';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

interface SettingsModalProps {
}
// Styling for the modal

const splitPriceParts = (price: string) => {
  const m = price.trim().match(/^([^\d\-+]*?)\s*([+-]?\d+(?:[.,]\d{1,2})?)\s*(.*)$/);
  if (!m) {
    return { prefix: '', major: price, minor: '', suffix: '' };
  }
  const [, prefix, num, suffix] = m;
  const sep = num.includes(',') ? ',' : '.';
  const [intPart, decPartRaw] = num.split(sep);
  const decPart = typeof decPartRaw === 'string' ? decPartRaw.padEnd(2, '0').slice(0, 2) : '';
  return {
    prefix: prefix || '',
    major: intPart,
    minor: decPart ? `${sep}${decPart}` : '',
    suffix: suffix || '',
  };
};

interface PriceCardProps {
  price: string;
}

const PriceCard: React.FC<PriceCardProps> = ({ price }) => {
  const { prefix, major, minor, suffix } = splitPriceParts(price);

  return (
    <Box
     // component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.3,
      }}
    >
      {prefix && <Typography variant="h6" sx={{ my: 1 }}>{prefix}</Typography>}
      {major && <Typography variant="h5" sx={{ my: 1 }}>{major}</Typography>}
      {minor && <Typography variant="h6" sx={{ my: 1, ml: '-3px' }}>{minor}</Typography>}
      {suffix && <Typography variant="h6" sx={{ my: 1 }}>{suffix}</Typography>}
    </Box>
  );
};

const TierCard = ({ title, price, features, currentTier, onSelect }: { title: string, price: string, features: string[], currentTier: string, onSelect: (tier: string) => void }) => {
  const isSelected = title === currentTier;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column', // enable vertical alignment
        justifyContent: 'space-between', // space out content and button
        textAlign: 'center',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderWidth: isSelected ? 2 : 1,
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.light',
        }
      }}
      onClick={() => onSelect(title)}
    >
      <Box>
        <Typography variant="h6" color={isSelected ? 'primary.main' : 'text.primary'}>{title}</Typography>
        <PriceCard price={price}/>
        <Box sx={{ my: 2 }}>
          {features.map((feature, index) => (
            <Typography key={index} variant="caption" display="block">{feature}</Typography>
          ))}
        </Box>
      </Box>

      {/* Push button to bottom */}
      <Button
        variant={isSelected ? 'contained' : 'outlined'}
        fullWidth
        onClick={(e) => { e.stopPropagation(); onSelect(title); }}
      >
        {isSelected ? 'Selected' : 'Select'}
      </Button>
    </Paper>
  );
};



const KnowledgebaseAdminTab: React.FC = () => {
  const { userId, setSessionId, setToken, setIsLoading, error: ctxErr, token } = useChat() as any;
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedKnowledgebase, setSelectedKnowledgebase] = useState<string>('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const fetchDatasets = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await listAllDatasets(userId, true, setSessionId, setToken);
      setDatasets(data.datasets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  }, [userId, setSessionId, setToken]);

  useEffect(() => { fetchDatasets(); }, [fetchDatasets]);

  const handleJobQueued = useCallback((queuedJobId: string) => {
    setJobId(queuedJobId);
    setUploading(true);
    setIsLoading(true);
  }, [setIsLoading]);

  const handleUploadDone = useCallback(() => {
    setUploading(false);
    setIsLoading(false);
    fetchDatasets();
  }, [fetchDatasets, setIsLoading]);

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FileUpload
          knowledgebaseName={selectedKnowledgebase}
          onKnowledgebaseNameChange={setSelectedKnowledgebase}
          onJobQueued={handleJobQueued}          // NEW
          onUploadSuccess={handleUploadDone}    // NEW
          setIsLoading={setUploading}
          existingDatasets={datasets.map(d => d.name)}
          userId={userId}
          orgDatasets
        />
      </Paper>

      {token && userId && jobId && (
        <ThinkingStreamPanel
          userId={userId}
          token={token}
          jobId={jobId}
          defaultExpanded
          maxHeight={320}
          hideAgents={[]}  // keep FileWorker + any agents
          title="Processing knowledgebase upload…"
        />
      )}

      <KnowledgebaseTable datasets={datasets} onRefresh={fetchDatasets} />
    </Box>
  );
};


const SettingsModal: React.FC<SettingsModalProps> = () => {
  const {
    isSettingsOpen,
    toggleSettingsModal,
    settingsTab,
    enabledPaths,
    updateEnabledPaths,
    activeDatasets,
    allDatasets,
    updateActiveDatasets,
    usageInfo,
    updateSubscriptionTier,
    isAdmin,
    licensedUsers,
    licenseQuota,
    assignLicense,
    removeLicense,
    isEnterpriseModalOpen,
    openEnterpriseModal,
    closeEnterpriseModal,
    saveEnterpriseDetails,
    userId,
    setSessionId, setToken,
    toggleShowAds,
  } = useChat();

  // Local state for pending changes
  const [localEnabledPaths, setLocalEnabledPaths] = useState(enabledPaths);
  const [localActiveDatasets, setLocalActiveDatasets] = useState<string[]>(activeDatasets);
  const [localTier, setLocalTier] = useState(usageInfo?.tier || 'Free');
  const [newEmail, setNewEmail] = useState('');
  const [isUserMemoriesOpen, setUserMemoriesOpen] = useState(false);
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));



  // Reset local state when modal opens or context data changes
  useEffect(() => {
    if (isSettingsOpen) {
      setLocalEnabledPaths(enabledPaths);
      setLocalActiveDatasets(activeDatasets);
      setLocalTier(usageInfo?.tier || 'Free');
    }
  }, [isSettingsOpen, enabledPaths, activeDatasets, usageInfo]);

  const handleLocalPathToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setLocalEnabledPaths({ ...localEnabledPaths, [name]: checked });
  };

  const handleLocalDatasetChange = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    setLocalActiveDatasets(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleAdsToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await toggleShowAds(event.target.checked);
  };

  const handleTierSelect = (tier: string) => {
    setLocalTier(tier);
    if (tier === 'Enterprise') {
      toggleSettingsModal(false);
      openEnterpriseModal();
    }
  };

  const handleSave = () => {
    // Compare and update if necessary
    if (JSON.stringify(localEnabledPaths) !== JSON.stringify(enabledPaths)) {
      updateEnabledPaths(localEnabledPaths);
    }
    if (JSON.stringify(localActiveDatasets) !== JSON.stringify(activeDatasets)) {
      updateActiveDatasets(localActiveDatasets);
    }
    if (localTier !== (usageInfo?.tier || 'Free') && localTier !== 'Enterprise') {
      updateSubscriptionTier(localTier);
    }
    toggleSettingsModal(false); // Close modal after saving
    trackEvent(AnalyticsEvents.SettingsSaved);
  };

  const handleCancel = () => {
    // Simply close the modal, discarding local changes
    toggleSettingsModal(false);
    trackEvent(AnalyticsEvents.SettingsCanceled);
  };

  const handleRemoveDataset = (name: string) => {
  setLocalActiveDatasets((prev) => prev.filter((n) => n !== name));
};
const NotesTab: React.FC = () => {
    const { userId, setSessionId, setToken } = useChat();
    const [isAdmin, setIsAdmin] = useState<boolean>(true); // TODO: Replace with actual admin check.

    const [notes, setNotes] = useState<{ [key: string]: { user_note: string; org_note: string } }>({});
    const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string | null>(null);

    const agents = [
        { id: 'TiAgent', name: 'Subjective Thinking (Ti)' },
        { id: 'FiAgent', name: 'Subjective Feeling (Fi)' },
        { id: 'TeAgent', name: 'Objective Thinking (Te)' },
        { id: 'FeAgent', name: 'Objective Feeling (Fe)' }
    ];

    const fetchAllNotes = useCallback(async () => {
        if (!userId) return;
        setIsLoading(prev => ({ ...prev, all: true }));
        try {
            const notePromises = agents.map(agent => getNotes(userId, agent.id, setSessionId, setToken));
            const allNotesData = await Promise.all(notePromises);
            const notesMap = allNotesData.reduce((acc, noteData, index) => {
                const agentId = agents[index].id;
                acc[agentId] = noteData || { user_note: '', org_note: '' };
                return acc;
            }, {} as typeof notes);
            setNotes(notesMap);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred fetching notes.');
        } finally {
            setIsLoading(prev => ({ ...prev, all: false }));
        }
    }, [userId, setSessionId, setToken]);

    useEffect(() => {
        fetchAllNotes();
    }, [fetchAllNotes]);

    const handleRefine = async (agentId: string) => {
        if (!userId) return;
        setIsLoading(prev => ({ ...prev, [agentId]: true }));
        setError(null);
        try {
            const result = await refineNotes(userId, agentId, setSessionId, setToken);
            setNotes(prev => ({ ...prev, [agentId]: result }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred while refining.');
        } finally {
            setIsLoading(prev => ({ ...prev, [agentId]: false }));
        }
    };

    const NoteCard = ({ title, agentId, noteType, showRefresh }: { title: string, agentId: string, noteType: 'user_note' | 'org_note', showRefresh: boolean }) => {
        const isRefining = !!isLoading[agentId] || !!isLoading['all'];
        const noteText = notes[agentId]?.[noteType] || 'No note found. Refine to generate one.';

        return (
            <Card variant="outlined">
                <CardHeader
                    title={title}
                    titleTypographyProps={{ variant: 'subtitle2' }}
                    action={
                        showRefresh && (
                            <IconButton onClick={() => handleRefine(agentId)} disabled={isRefining} size="small" title={`Refine ${title}`}>
                                <RefreshIcon fontSize="small" sx={{ animation: isRefining ? 'spin 1s linear infinite' : 'none' }} />
                            </IconButton>
                        )
                    }
                    sx={{ pb: 0, minHeight: '48px' }}
                />
                <Divider />
                <CardContent>
                    {isRefining && !notes[agentId]?.[noteType] ? (
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    ) : (
                        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '60px', display: 'block' }}>
                            {noteText}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customize Enantio's responses by refining its self-guidance notes. Click the refresh icon to generate notes from your recent memories.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* A Box using raw flexbox for layout */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                {agents.map((agent) => (
                    // Each agent card is a flex item.
                    <Box key={agent.id} sx={{ flex: '1 1 180px', maxWidth: '400px' }}>
                         <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <Typography variant="h6" gutterBottom align="center">{agent.name}</Typography>
                            
                            {/* Inner stack for vertical notes remains the same. */}
                            {userId && (
                               <Stack spacing={2}>
                                   <NoteCard title="Personal Note" agentId={agent.id} noteType="user_note" showRefresh={true} />
                               </Stack>
                            )}
                        </Paper>
                    </Box>
                ))}
            </Box>

             <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Box>
    );
};

  return (
    <>
    <Modal
      //={{ zIndex: 2002 }}
      open={isSettingsOpen}
      onClose={handleCancel} // Use handleCancel on backdrop click
      aria-labelledby="settings-modal-title"
    >
      <Box sx={{
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: isMobile ? 300: 600,
  bgcolor: 'background.paper',
  border: '1px solid #555',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
   maxHeight: '90vh',   // Limit the modal's max height to 90% of the viewport
}}>
        <Tabs variant="fullWidth" value={settingsTab} onChange={(_, v) => toggleSettingsModal(true, v)} sx={{
    mb: 2,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center', // center the whole tabs group
      '& .MuiTabs-scroller': { display: 'flex', justifyContent: 'center' },
    '& .MuiTabs-flexContainer': { justifyContent: 'center' },
  }}
  >
          <Tab label="Settings" value="settings" />
          <Tab label="Account" value="account" />
          <Tab label="Memories" value="memories" />
          <Tab label="Notes" value="notes" />
          {usageInfo?.tier == "Pro" || usageInfo?.tier == "Enterprise" && (<Tab label="Personality" value="personality" />)}
          {isAdmin && (
            <Tab
              label={`Licenses (${licensedUsers.length}/${licenseQuota})`}
              value="licenses"
            />
          )}
          {isAdmin && (
            <Tab label="Knowledgebase" value="knowledgebase" />
          )}
        </Tabs>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
          {settingsTab === 'settings' && (
            <>
              <Typography variant="overline">Cognitive Paths</Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Switch checked={localEnabledPaths.Si} onChange={handleLocalPathToggle} name="Si" />}
                  label="Enable Si (Concrete impressions)"
                />
                <FormControlLabel
                  control={<Switch checked={localEnabledPaths.Ne} onChange={handleLocalPathToggle} name="Ne" />}
                  label="Enable Ne (Creative Exploration)"
                />
                <FormControlLabel
                  control={<Switch checked={localEnabledPaths.Ni} onChange={handleLocalPathToggle} name="Ni" />}
                  label="Enable Ni (Abstract impressions)"
                />
              </FormGroup>

              <Divider sx={{ my: 2 }} />
               {usageInfo?.tier === 'Free' && (
                <FormGroup sx={{ alignItems: 'center', mb: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={usageInfo?.showAds ?? false} onChange={handleAdsToggle} />}
                    label="Enable ads to double thought limit"
                  />
                </FormGroup>
              )}

              {
                allDatasets.length > 0 && (
                  <Box>
                    <Typography variant="overline">Active Datasets</Typography>
              <Select
                multiple
                fullWidth
                value={localActiveDatasets}
                onChange={handleLocalDatasetChange}
                input={<OutlinedInput label="Active Datasets" />}
                 MenuProps={{
                  slotProps: { paper: { sx: { zIndex: 2100 } } }, // v5+ way
                  PaperProps: { sx: { zIndex: 2100 } },        // alt if on older v5
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" onDelete={() => handleRemoveDataset(value)}/>
                    ))}
                  </Box>
                )}
              >
                {allDatasets.map((name: string) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
                  </Box>
                )
              }
            </>
          )}
          {settingsTab === 'account' && (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, my: 2 }}>
                <TierCard
                  title="Free"
                  price="€0/mo"
                  features={['25', 'thoughts/week', 'gpt-5-mini']}
                  currentTier={localTier}
                  onSelect={handleTierSelect}
                />
                 <TierCard
                  title="Starter"
                  price="€27.95/mo"
                  features={['250','thoughts/week', 'gpt-5']}
                  currentTier={localTier}
                  onSelect={handleTierSelect}
                />
                <TierCard
                  title="Pro"
                  price="€49.95/mo"
                  features={['500', 'thoughts/week', 'gpt-5', '& custom personalities']}
                  currentTier={localTier}
                  onSelect={handleTierSelect}
                />
                <TierCard
                  title="Enterprise"
                  price="€149.95/mo"
                  features={['1000', 'thoughts/week', 'gpt-5', '& custom knowledgebase']}
                  currentTier={localTier}
                  onSelect={handleTierSelect}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              {usageInfo && (
                <Typography variant="body2" textAlign="center" sx={{mb: 2}}>
                  {usageInfo.usage} / {usageInfo.limit === -1 ? '∞' : usageInfo.limit} thoughts used
                </Typography>
              )}
            </>
          )}
         {settingsTab === 'memories' && (
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Review or delete the personalized memories Enantio has learned from your conversations.
                </Typography>
                {userId ? (
                  <ManageMemories sessionId={userId} />
                ) : (
                  <Alert severity="warning">User ID not found. Cannot load memories.</Alert>
                )}
              </Box>
            )}
             {settingsTab === 'notes' && (
            <NotesTab />
          )} 
          {settingsTab === 'personality' && (
            <ManagePersonalities />
          )}
          {settingsTab === 'licenses' && isAdmin && (
            <>
              <Typography variant="overline">Licensed Users</Typography>
              <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="New user email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newEmail) {
                      assignLicense(newEmail);
                      setNewEmail('');
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newEmail) {
                      assignLicense(newEmail);
                      setNewEmail('');
                    }
                  }}
                  disabled={!newEmail}
                >
                  Add
                </Button>
              </Box>
              <List>
                {licensedUsers.map((email: string) => (
                  <ListItem key={email} secondaryAction={
                    <IconButton edge="end" onClick={() => removeLicense(email)}>
                      <DeleteIcon />
                    </IconButton>
                  }>
                    <ListItemText primary={email} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
           {settingsTab === 'knowledgebase' && isAdmin && (
            <KnowledgebaseAdminTab />
          )}
        </Box>
        
        <Box sx={{ pt: 2, flexShrink: 0 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Confirm</Button>
            </Box>
        </Box>

      </Box>
    </Modal>
     <EnterpriseModal
        open={isEnterpriseModalOpen}
        onClose={closeEnterpriseModal}
        onSave={saveEnterpriseDetails}
      />
      </>
  );
};

export default SettingsModal;